import { SelectorChain, SelectorPart } from '@fullstory/proto/fs/selectors';
import { nil } from '@fullstory/typeutils';
import {
  newSelectorChain,
  newSelectorPart,
  serializeSelector,
  parseSelector,
} from './selector_parser';

// Code for deriving selectors from elements suitable for use in block lists, searches, and human-readable UIs.
// TODO(jaime): Is this the best place for this?

export const INSPECTOR_HOVER_CLASS = '_fs_inspect_hover';
export const INSPECTOR_MATCHED_CLASS = '_fs_inspect_matched';

// Classes that we add to the DOM during playback that shouldn't show up in the selector string.
//
// The data-lp-ignore attribute is part of the LastPass browser extension's API; it can
// be present on customer pages but is also added by the FS renderer.
//
// TODO(sethf): Refactor things so that the playback code can maintain this
// list; this is inevitably going to get out of sync with playback if it lives
// here.

const fsAddedClassesAndAttrs = /(?:^_fs_)|(^data-lpignore$)/;

const removeTheseAttr = new Set<string>([
  'style',
  'label',

  // The "xmlns" attribute is special in that it lives in its own namespace; the
  // attribute "xmlns" thus implicitly means "xmlns:xmlns". See here:
  //   https://www.w3.org/2000/xmlns/
  // FullStory code is generally not namespace-aware, although we have special
  // support for the SVG namespace, so we're unable to handle "xmlns" correctly.
  // In particular, the selector `svg[xmlns]` does not match anything; it would
  // actually need to be `svg[*|xmlns]` to work, but we don't have any way of
  // capturing that distinction. Rather than add a bunch of complexity to
  // support this attribute, we just drop it in generated selectors.
  'xmlns',
]);

export interface SelectorMatch {
  Selector: string;
  HumanReadableSelector?: string;
  Count?: number;
  Mobile?: boolean
}

export function SelectorForElem(e: HTMLElement | null, initial: boolean = true): SelectorChain | null {
  if (!e) {
    return null;
  }
  const rule: SelectorPart = newSelectorPart();
  const chain: SelectorChain = newSelectorChain(SelectorForElem(e.parentElement, false), e.parentElement ? ' ' : '', rule);
  rule.tag = e.tagName.toLowerCase();

  if (!chain.parent) {
    chain.combinator = '';
  }

  // No mapping for standalone root elements
  if (initial && (rule.tag === 'body' || rule.tag === 'html')) {
    return null;
  }
  const idVal = e.getAttribute('id');
  if (idVal) {
    rule.id = idVal;
  }

  for (const attrVal of e.attributes) {
    if (attrVal.name === 'class' || attrVal.name === 'id' || removeTheseAttr.has(attrVal.name) || attrVal.name.match(fsAddedClassesAndAttrs)) {
      continue;
    }
    rule.attrs[attrVal.name] = attrVal.value;
  }

  if (rule.tag == 'a') {
    const hrefAttr = rule.attrs['href'];
    if (hrefAttr) {
      rule.attrs['href'] = undoSvgIdHack(hrefAttr);
    }
  }

  // Set the attrMatch operator.
  Object.keys(rule.attrs).forEach(attr => {
    rule.attrMatch[attr] = '=';
  });

  for (const clazz of e.classList) {
    if (!clazz.match(fsAddedClassesAndAttrs) && !isInvalidClass(clazz)) {
      rule.classes.push(clazz);
    }
  }
  chain.rule = rule;
  return chain;
}

// UrlRewriter will add the current URL in front of the hash fragment for hrefs and xlink:hrefs
// to work around SVG resource ID references breaking due to mismatch between base url and page url.
// This undoes that so that generated selectors aren't all completely screwed up.
// TODO(sethf): This is going to be somewhat useless in a playback service
// worker world since HTTPS URLs never get rewritten and HTTP URLs get rewritten
// in a completely different way. Rather than maintaining logic to invert the
// transformations that UrlRewriter applies here, we should just figure out some
// mechanism for exposing the original URL to code that needs it. (This is yet
// another situation where a cleaner DOM/vDOM separation in playback would
// help.)
function undoSvgIdHack(href: string): string;
function undoSvgIdHack(href: null): null;
function undoSvgIdHack(href: string | null): string | null;
function undoSvgIdHack(href: string | null): string | null {
  if (!href) {
    return href;
  }
  return href.replace(`${window.location.href}#`, '#');
}

function isInvalidClass(c: string): boolean {
  // TODO(jaime): Filter out more stuff here.
  return c.indexOf('[') > 0;
}

export function ToSelectorParts(sel: SelectorChain | null): SelectorPart[] {
  const parts: SelectorPart[] = [];
  let curr = sel;
  while (curr) {
    if (curr.rule) {
      parts.push(curr.rule);
    }
    curr = curr.parent;
  }
  return parts.reverse();
}

// Returns similar elements for a given selector that try to err on "simpler" where simpler can mean shorter and/or
// fewer components.
// WARNING: Below DOES SLOW SHIT.
//
// TODO(jaime): Make this less hacky. Potentially add more (better) heuristics.
// NOTE that the combinatorial space here is potentially very large. Something like
// (N choose M for each level) * N * number of levels where N is the
// total number of components to the selector at a tier and M is the surviving number of components.
export function SimilarSelectors(sel: SelectorChain, limit: number = 3): SelectorMatch[] {
  sel = conservativeShorten(sel, limit);

  const rtn = [];
  const derivedSelectorSet = new Set<string>(); // Don't add the same selector more than once.

  // The original one.
  derivedSelectorSet.add(serializeSelector(sel));

  // 1. Just the last SelectorPart
  const lastPart: SelectorChain = newSelectorChain(null, '');
  copyRule(sel.rule, lastPart.rule);
  const s = serializeSelector(lastPart);
  rtn.push({ Selector: s });
  derivedSelectorSet.add(s);

  // 2. Depth limited 2 walking backwards.
  const shorterStr = serializeSelector(takeRightN(sel, 2));
  if (!derivedSelectorSet.has(shorterStr)) {
    rtn.push({ Selector: shorterStr });
    derivedSelectorSet.add(shorterStr);
  }

  // 3. No more than one class per tier. Also no attributes.
  const reducedClasses = classReduce(sel);
  const reducedClassesStr = serializeSelector(reducedClasses);
  if (!derivedSelectorSet.has(reducedClassesStr)) {
    rtn.push({ Selector: reducedClassesStr });
    derivedSelectorSet.add(reducedClassesStr);
  }

  // 4. Depth limited 2 again.
  const shorterReducedStr = serializeSelector(takeRightN(reducedClasses, 2));
  if (!derivedSelectorSet.has(shorterReducedStr)) {
    rtn.push({ Selector: shorterReducedStr });
    derivedSelectorSet.add(shorterReducedStr);
  }

  return rtn;
}

// Returns a new chain of SelectorRules that serializes to a nicer meat eyeball friendly string.
export function HumanReadable(sel: SelectorChain, limit: number = 3): SelectorChain {
  const nicer = conservativeShorten(sel, limit);
  forEachRule(nicer, cur => {
    if (!cur.rule) {
      return false;
    }
    // Visit s.
    // Remove the tag names if there is a class.
    if (cur.rule.classes.length > 0) {
      cur.rule.tag = '';
    }
    return false; // keep going.
  });
  return nicer;
}

export function trimOnValidID(chain: SelectorChain): boolean {
  if (chain.rule?.id) {
    if (!isNaN(parseInt(chain.rule.id.charAt(0)))) {
      // This starts with either a number, or "+1"
      // Disallow this as a valid ID and let things continue.
      chain.rule.id = '';
    } else {
      // Valid ID.
      // Stop when we hit an ID. And make it so the only thing we serialize is the ID.
      chain.rule.attrs = {};
      chain.rule.classes = [];
      chain.rule.attrMatch = {};
      chain.rule.tag = '';
      chain.parent = null;
      chain.combinator = '';
      return true;
    }
  }

  return false;
}

// Returns a potentially shortened form of this selector that stops at IDs if it finds one. Also does a simple depth limit
// since we need to emit something that is amenable to the element block list. That can't be too long or else slowness.
// Lowest common denominator and all.
//
// The intent here is not to change the set of elements that matches.
// We employ a few simple heuristics:
// 1. If we find an ID, stop.
// 2. body and above are redundant.
// 3. More than 3 tiers of hierarchy are probably too much. This heuristic can potentially alter the match set, but we show the
//    match set visually, so this might not be a big problem.
//
// TODO(jaime): This can, and probably should, get more sophisticated.
export function conservativeShorten(sel: SelectorChain, limit: number): SelectorChain {
  // Take the right N parts.
  let d = 0;
  const shortened = cloneChain(sel);
  forEachRule(shortened, chain => {
    if (d === limit - 1) {
      chain.parent = null;
      chain.combinator = '';
      return true; // stop
    }
    if (trimOnValidID(chain)) {
      return true;
    }
    d++;
    return false; // Don't stop
  });
  return shortened;
}

// Returns a modified version of the selector that only contains a single class name.
// Does NOT mutate the input selector.
function classReduce(selector: SelectorChain): SelectorChain {
  const reduced = cloneChain(selector);
  forEachRule(reduced, chain => {
    if (!chain.rule) {
      return false;
    }
    const classes = [...chain.rule.classes];
    if (classes.length > 0) {
      classes.sort(); // sort for display purposes
      chain.rule.classes = [String(classes[0])];
    }
    return false; // Keep going. Don't stop.
  });
  return reduced;
}

// Makes a copy of the SelectorRule, recursively walking up and cloning all parent rules too.
export function cloneChain(rule: SelectorChain): SelectorChain;
export function cloneChain(rule: SelectorChain | nil): SelectorChain | null;
export function cloneChain(rule: SelectorChain | nil): SelectorChain | null {
  if (!rule) {
    return null;
  }

  const newRule = newSelectorChain(cloneChain(rule.parent), rule.parent ? rule.combinator : '');
  copyRule(rule.rule, newRule.rule);
  return newRule;
}

function copyRule(from: SelectorPart | nil, to: SelectorPart | nil): void {
  if (!from || !to) {
    return;
  }

  to.tag = from.tag;
  to.id = from.id;
  from.classes.forEach(clazz => {
    to.classes.push(clazz);
  });
  Object.entries(from.attrs).forEach(([key, value]) => {
    to.attrs[key] = value;
  });
  Object.entries(from.attrMatch).forEach(([key, value]) => {
    to.attrMatch[key] = value;
  });
}

// Take the right N parts.
function takeRightN(sel: SelectorChain, n: number): SelectorChain {
  let d = 0;
  const depthLimited = cloneChain(sel);
  forEachRule(depthLimited, rule => {
    if (d === n - 1) {
      rule.parent = null;
      rule.combinator = '';
      return true; // stop
    }
    d++;
    return false; // Don't stop
  });
  return depthLimited;
}

export function forEachRule(rules: SelectorChain, visit: (rule: SelectorChain) => boolean): void {
  let cur: SelectorChain | nil = rules;
  while (cur) {
    const stop = visit(cur);
    if (stop) { return; }
    cur = cur.parent;
  }
}

export interface SelectorChainRule {
  rule: SelectorPart,
  // combinator indicates the combinator when combined with next/descendent rule.
  // Note: this is reverse of standard SelectorChain semantics which traverses from child to ancestor.
  combinator: string,
}

export function makeOrderedSelectorRules(sel: SelectorChain): SelectorChainRule[] {
  const rules: SelectorChainRule[] = [];
  let last: SelectorChain | null = null;
  // map to selector rules
  for (let curr: SelectorChain | null = sel; curr; curr = curr.parent) {
    if (!curr.rule) {
      // shouldn't happen, but if it does let's stop here
      break;
    }
    rules.push({ rule: curr.rule, combinator: last ? last.combinator : '' });
    last = curr;
  }
  // then reverse since selector chains are reverse order
  rules.reverse();
  return rules;
}

export function compareSelectorDepths(a: number[], b: number[]): number {
  const least = Math.min(a.length, b.length);
  // work our way back from the last term
  // lengths can be different since selectors could have a different number of parts
  // more than likely the deepest depth will be different but we will keep falling back until we check all parts
  for (let di = 1; di <= least; di++) {
    const da = a[a.length - di]!;
    const db = b[b.length - di]!;
    if (da !== db) {
      // deeper depth is considered "less" and defaults to sorting first
      return da > db ? -1 : 1;
    }
  }
  // if we reached here, all applicable depths are the same
  // whichever has more components at this point should come first
  return b.length - a.length;
}

export function containsNonDescendantCombinator(selector: string): boolean {
  const hasNonDescendantCombinator = (selectorChain: SelectorChain | null): boolean => {
    if (selectorChain === null) {
      return false;
    }

    if (selectorChain.combinator.trim() !== '') {
      return true;
    }

    return hasNonDescendantCombinator(selectorChain.parent);
  };

  try {
    return hasNonDescendantCombinator(parseSelector(selector));
  } catch (e: any) {
    console.warn('Failed to parse selector:', selector, e.message);
    return false;
  }
}
